import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftRight, ChevronDown, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOfflineData } from "@/contexts/OfflineDataContext";

const SERVICE_LABELS = { ground: "PCP/ACP-G", sfa_emr: "SFA/EMR", wes: "PCP-PS", air_adult: "Air Adult", air_pediatric: "Air Peds" };
const SERVICE_COLORS = { ground: "bg-blue-600", sfa_emr: "bg-emerald-600", wes: "bg-amber-600", air_adult: "bg-cyan-600", air_pediatric: "bg-pink-600" };
const SERVICE_ORDER = ["ground", "sfa_emr", "wes", "air_adult", "air_pediatric"];

function CareLevelSummary({ protocol }) {
  return (
    <div className="space-y-2">
      {(protocol.care_levels || []).map((cl, i) => (
        <div key={i}>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">{cl.title || cl.level}</div>
          <ul className="space-y-0.5">
            {(cl.interventions || []).map((iv, j) => (
              <li key={j} className="text-sm">
                <span>{iv.text}</span>
                {iv.sub_steps?.length > 0 && (
                  <ul className="ml-4 mt-0.5 space-y-0.5">
                    {iv.sub_steps.map((s, k) => (
                      <li key={k} className="text-xs text-muted-foreground">- {s}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function CrossServiceCompare() {
  const { protocols } = useOfflineData();
  const [selectedName, setSelectedName] = useState("");

  // Build map: normalized name -> { service: protocol }
  const nameMap = useMemo(() => {
    const map = {};
    for (const p of protocols) {
      const key = p.name.toLowerCase().trim();
      if (!map[key]) map[key] = {};
      map[key][p.service || "ground"] = p;
    }
    return map;
  }, [protocols]);

  // Only show names that exist in 2+ services
  const comparableNames = useMemo(() => {
    return Object.entries(nameMap)
      .filter(([_, services]) => Object.keys(services).length >= 2)
      .map(([name, services]) => ({
        name,
        displayName: Object.values(services)[0].name,
        serviceCount: Object.keys(services).length,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [nameMap]);

  const matchedProtocols = selectedName ? nameMap[selectedName] || {} : {};
  const activeServices = SERVICE_ORDER.filter(s => matchedProtocols[s]);

  return (
    <Card data-testid="cross-service-compare">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg font-heading">
          <ArrowLeftRight className="w-5 h-5 text-primary" />
          Compare Across Services
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Select a protocol to compare how it differs between guideline sets
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <select
            value={selectedName}
            onChange={(e) => setSelectedName(e.target.value)}
            className="w-full px-3 py-2 rounded-md border bg-background text-sm appearance-none pr-8"
            data-testid="compare-select"
          >
            <option value="">Select a protocol ({comparableNames.length} available)...</option>
            {comparableNames.map((item) => (
              <option key={item.name} value={item.name}>
                {item.displayName} ({item.serviceCount} services)
              </option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        {activeServices.length >= 2 && (
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(activeServices.length, 3)}, 1fr)` }}>
            {activeServices.map((service) => {
              const p = matchedProtocols[service];
              return (
                <div key={service} className="border rounded-lg overflow-hidden" data-testid={`compare-col-${service}`}>
                  <div className="px-3 py-2 border-b flex items-center justify-between gap-2">
                    <Badge className={`${SERVICE_COLORS[service]} text-white text-[10px]`}>
                      {SERVICE_LABELS[service]}
                    </Badge>
                    <Link to={`/protocol/${p.id}`} className="text-xs text-primary hover:underline flex items-center gap-1">
                      Open <ExternalLink className="w-3 h-3" />
                    </Link>
                  </div>
                  <div className="p-3 max-h-[400px] overflow-y-auto">
                    <CareLevelSummary protocol={p} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedName && activeServices.length < 2 && (
          <p className="text-center text-muted-foreground text-sm py-4">This protocol only exists in one service.</p>
        )}
      </CardContent>
    </Card>
  );
}
